<template>
    <div class="mt-24">
        <div class="grid grid-cols-2 gap-x-32 gap-y-16">
            <div
                v-for="(field, index) in fields.filter(f => (showAll ? true : (f.required || f.type === 'STATISCH')) && validFieldRules(f))"
                :key="field.name"
                :class="field.type === 'STATISCH' ? 'col-span-2' : 'col-span-1'"
            >
                <p v-if="field.type === 'STATISCH'"
                   class="text-body font-semibold"
                   :class="index === 0 ? 'mt-0' : 'mt-16'"
                   v-html="label(field)"
                ></p>
                <multi-input
                    v-if="field.type === 'TEXTEXPAND'"
                    :value="form.data[field.name]"
                    :errors="form.errors[field.name]"
                    @input="setFormValue(field.name, $event)"
                >
                    {{ label(field) }}
                </multi-input>
                <simple-input
                    v-if="field.type === 'TEXT'"
                    :value="form.data[field.name]"
                    :errors="form.errors[field.name]"
                    @input="setFormValue(field.name, $event)"
                >
                    {{ label(field) }}
                </simple-input>
                <multi-level-dropdown
                    v-else-if="field.type === 'HIER'"
                    :ref="field.name"
                    :options="field.options"
                    :value="form.data[field.name]"
                    :errors="form.errors[field.name]"
                    @input="setFormValue(field.name, $event)"
                    @load="searchAutocomplete(field, $event)"
                >
                    {{ label(field) }}
                </multi-level-dropdown>
                <select-dropdown
                    v-else-if="field.type === 'SELECT' || field.type === 'MULTIPLE' || field.type === 'AUTOCOMPLETE'"
                    :value="form.data[field.name]"
                    :loading="fetching.includes(field.name)"
                    :multiple="field.type === 'MULTIPLE'"
                    :options="fieldOptions(field)"
                    :autoTranslate="false"
                    :keepSearchAfterSelection="field.type === 'AUTOCOMPLETE'"
                    searchable
                    remove-icon
                    autoload
                    :errors="form.errors[field.name]"
                    @search="searchAutocomplete(field, $event)"
                    @change="setFormValue(field.name, $event)"
                >
                    {{ label(field) }}
                </select-dropdown>
                <radio-button
                    v-else-if="field.type === 'RADIO'"
                    :value="form[field.name]"
                    :checked="1"
                    :errors="form.errors[field.name]"
                    @change="setFormValue(field.name, $event)"
                >
                    {{ label(field) }}
                </radio-button>
                <date-picker
                    v-else-if="field.type === 'DATE'"
                    :value="form[field.name]"
                    :errors="form.errors[field.name]"
                    @input="setFormValueDate(field.name, $event)"
                >
                    {{ label(field) }}
                </date-picker>
                <simple-textarea
                    v-else-if="field.type === 'TEXTAREA'"
                    :value="form[field.name]"
                    :errors="form.errors[field.name]"
                    @input="setFormValue(field.name, $event)"
                >
                    {{ label(field) }}
                </simple-textarea>
                <wysiwyg
                    v-else-if="field.type === 'HTMLAREA'"
                    :value="form[field.name]"
                    :errors="form.errors[field.name]"
                    @input="setFormValue(field.name, $event)"
                >
                    {{ label(field) }}
                </wysiwyg>
            </div>
        </div>
    </div>
</template>

<script>
import SelectDropdown from "../../forms/SelectDropdown.vue";
import SimpleInput from "../../forms/Input.vue";
import DatePicker from "../../forms/DatePicker.vue";
import SimpleTextarea from "../../forms/TextArea.vue";
import Wysiwyg from "../../forms/Wysiwyg.vue";
import RadioButton from "../../forms/RadioButton.vue";
import {mapActions, mapState} from "pinia";
import {useMultiPostingStore} from '../../../state/modules/multiPosting';
import MultiLevelDropdown from "../../analytics/MultiLevelDropdown.vue";
import MultiInput from "../../forms/MultiInput.vue";
import moment from 'moment';

export default {
    name: 'ChannelRequirements',
    components: {
        MultiInput,
        MultiLevelDropdown,
        RadioButton, SimpleInput, SelectDropdown, DatePicker, SimpleTextarea, Wysiwyg},
    props: {
        contractId: [Number, String],
        showAll: {
            type: Boolean,
            default: false
        },
        fields: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            fetching: [],
            activeSearches: new Map(),
        }
    },
    computed: {
        ...mapState(useMultiPostingStore, ['postingRequirementForms']),
        form() {
            return this.postingRequirementForms[this.contractId];
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, ['setPostingRequirementFormValue', 'fetchContractOptions']),
        label(field) {
            let required = '';
            if (field.required) required += '*';
            return field.label + required;
        },
        searchAutocomplete(field, query) {
            if (!this.requiresLoading(field)) return;
            if (this.activeSearches[field.name]) {
                clearTimeout(this.activeSearches[field.name]);
            }
            this.activeSearches[field.name] = setTimeout(() => {
                this.fetching.push(field.name);
                const shouldSlice = false
                this.fetchContractOptions({contractId: this.contractId, postingRequirementName: field.name, query, shouldSlice})
                    .then(() => this.fetching = this.fetching.filter(x => x !== field.name))
                    .then(() => this.reload(field.name));
            }, 250);
        },
        requiresLoading(field) {
            return field.type === 'AUTOCOMPLETE'
                || (field.type === 'HIER' && field.autocomplete)
                || (field.type === 'SELECT' && field.autocomplete);
        },
        reload(fieldName) {
            if (this.$refs[fieldName] && typeof this.$refs[fieldName][0].reload === 'function') {
                this.$refs[fieldName][0].reload();
            }
        },
        fieldOptions(field) {
            if (!field.options) return [];
            return field.options.map(x => ({label: x.label, value: x.key}));
        },
        setFormValueDate(name, value) {
            if (value) {
                value = moment(value).format("YYYY-MM-DD")
            }
            this.setFormValue(name, value)
        },
        setFormValue(name, value) {
            if (Array.isArray(this.form[name]) && !Array.isArray(value)) {
                value = [value];
            }
            this.setPostingRequirementFormValue({contractId: this.contractId, postingRequirementName: name, value});
        },
        validFieldRules(field) {
            if (!field.display_rules) return true;
            let show = true;
            field.display_rules.show.forEach(rule => {
                // skip all rules once show is false
                if (!show) return;
                if (!this.form[rule.facet]) show = false;
                else show = this.ruleOperator(this.form[rule.facet], rule);
            });
            return show;
        },
        ruleOperator(value, rule) {
            switch (rule.op) {
                case 'in':
                    return rule.value.includes(value);
                case 'notempty':
                    return !!value;
                case 'equal':
                    return value === rule.value;
                case 'contains':
                    return value.includes(rule.value);
                default:
                    return false;
            }
        }
    }
}
</script>
